import Pagination from '@/components/Pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import api from '@/lib/axiosInstance';
import Helper from '@/lib/helper';
import type { PropertyListingDocuments } from '@/types/PropertyListingDocuments';
import { motion } from 'framer-motion';
import { Edit, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

type DocumentTab = 'all' | 'pending' | 'completed';

const PAGE_SIZE = 10;

const DocumentsPage = () => {
    const menu: DocumentTab[] = ['all', 'pending', 'completed'];
    const [documents, setDocuments] = useState<PropertyListingDocuments[]>([]);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState<DocumentTab>('all');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [documentToDelete, setDocumentToDelete] = useState<PropertyListingDocuments | null>(null);
    const [deleting, setDeleting] = useState(false);

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            const response = await api.get('/documents');
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            setDocuments(response.data.documents || []);
        } catch (err) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, []);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchInput]);

    const templateName = (documentTemplateId: number) => {
        const names: Record<number, string> = {
            1: 'General Authorization to sell',
            2: 'General Authorization to let',
            3: 'Agreement to purchase',
            4: 'Agreement to rent',
            5: 'Letter of undertaking',
            6: 'Exclusive authorization to sell',
            7: 'Exclusive authorization to rent',
            8: 'Co-Agency agreement',
        };
        return names[documentTemplateId] || 'Document';
    };

    const getDocumentState = (doc: PropertyListingDocuments): DocumentTab => {
        const normalized = String(doc.status || '').toLowerCase();
        if (normalized === 'completed' || doc.completedAt || String(doc.status) === '1') {
            return 'completed';
        }
        return 'pending';
    };

    const counts = useMemo(() => {
        const pending = documents.filter((doc) => getDocumentState(doc) === 'pending').length;
        const completed = documents.filter((doc) => getDocumentState(doc) === 'completed').length;
        return {
            all: documents.length,
            pending,
            completed,
        };
    }, [documents]);

    const filteredDocuments = useMemo(() => {
        return documents.filter((doc) => {
            const matchesTab = active === 'all' ? true : getDocumentState(doc) === active;
            if (!matchesTab) return false;

            if (!search) return true;
            const haystack = [
                templateName(doc.documentTemplateId),
                doc.listing?.listingReferenceNumber || '',
                doc.listing?.headline || '',
                ...((doc.signers || []).map((signer) => `${signer.name} ${signer.email}`)),
            ].join(' ').toLowerCase();

            return haystack.includes(search.toLowerCase());
        });
    }, [documents, active, search]);

    const paginatedDocuments = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredDocuments.slice(start, start + PAGE_SIZE);
    }, [filteredDocuments, page]);

    const totalPages = Math.max(1, Math.ceil(filteredDocuments.length / PAGE_SIZE));

    useEffect(() => {
        if (page > totalPages) {
            setPage(1);
        }
    }, [page, totalPages]);

    const getStatusBadge = (doc: PropertyListingDocuments) => {
        const state = getDocumentState(doc);
        if (state === 'completed') {
            return (
                <Badge variant="outline" className="border-green-300 bg-green-50 text-green-700">
                    Completed
                </Badge>
            );
        }

        return (
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
                Pending
            </Badge>
        );
    };

    const handleTabClick = (tab: DocumentTab) => {
        setActive(tab);
        setPage(1);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setSearch('');
        setPage(1);
    };

    const handleDeleteDocument = async () => {
        if (!documentToDelete?.listingId) return;

        try {
            setDeleting(true);
            const response = await api.delete(`/listings/${documentToDelete.listingId}/documents/${documentToDelete.id}`);
            if (!response.data.status) {
                throw new Error(response.data.error);
            }

            toast.success('Document deleted successfully');
            setDocuments((prev) => prev.filter((doc) => doc.id !== documentToDelete.id));
        } catch (err) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setDeleting(false);
            setDocumentToDelete(null);
        }
    };

    return (
        <div>
            <div className='mb-5 flex items-center justify-between'>
                <h5 className='h5 uppercase bold'>Documents</h5>
            </div>

            <div className='mb-5'>
                <div className='relative max-w-md'>
                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400' size={18} />
                    <Input
                        type='text'
                        placeholder='Search by document, listing, signer...'
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className='pl-10 pr-10'
                    />
                    {searchInput && (
                        <Button
                            variant='ghost'
                            size='sm'
                            onClick={handleClearSearch}
                            className='absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0'
                        >
                            <X size={16} />
                        </Button>
                    )}
                </div>
                {searchInput && (
                    <p className='mt-1 text-xs text-muted-foreground'>Searching for "{searchInput}"...</p>
                )}
            </div>

            <div className='md:flex'>
                {menu.map((item) => (
                    <div
                        key={item}
                        onClick={() => handleTabClick(item)}
                        className={`relative z-10 mx-1 px-1 py-2 transition-colors cursor-pointer md:mx-3 md:px-3 ${
                            active === item ? 'font-bold text-black' : 'text-gray-600'
                        }`}
                    >
                        <span className={`relative z-10 flex items-center gap-2 text-xs uppercase ${active === item ? 'text-black' : 'text-gray-400'}`}>
                            {item}
                            <span
                                className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
                                    active === item ? 'bg-black text-white' : 'bg-gray-200 text-gray-600'
                                }`}
                            >
                                {counts[item]}
                            </span>
                        </span>
                        {active === item && (
                            <motion.div
                                layoutId='underline-documents'
                                className='absolute bottom-0 left-0 right-0 h-[2px] rounded-full bg-black'
                            />
                        )}
                    </div>
                ))}
            </div>

            <Table className='mt-5'>
                <TableHeader>
                    <TableRow>
                        <TableHead className='w-[80px]'>No</TableHead>
                        <TableHead>Document</TableHead>
                        <TableHead>Listing</TableHead>
                        <TableHead>Signers</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className='text-right'>Action</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={7}>
                                <div className='flex justify-center py-4'>
                                    <Spinner />
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : paginatedDocuments.length > 0 ? (
                        paginatedDocuments.map((doc, index) => {
                            const completedCount = doc.signers?.filter((signer) => ['completed', 'signed'].includes(String(signer.status).toLowerCase())).length || 0;
                            const totalCount = doc.signers?.length || 0;
                            const hasListing = !!doc.listingId;

                            return (
                                <TableRow key={doc.id}>
                                    <TableCell>{(page - 1) * PAGE_SIZE + (index + 1)}</TableCell>
                                    <TableCell>
                                        <div>
                                            <p className='font-medium text-slate-900'>{templateName(doc.documentTemplateId)}</p>
                                            <p className='text-xs text-slate-500'>#{doc.id}</p>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {hasListing ? (
                                            <div>
                                                <p>{doc.listing?.listingReferenceNumber || `Listing #${doc.listingId}`}</p>
                                                {doc.listing?.headline && (
                                                    <p className='line-clamp-1 text-xs text-slate-500'>{doc.listing.headline}</p>
                                                )}
                                            </div>
                                        ) : (
                                            <span className='text-slate-400'>-</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div>
                                            <p>{completedCount} / {totalCount} completed</p>
                                            <p className='text-xs text-slate-500'>
                                                {doc.signers?.[0]?.name || 'No signers'}
                                                {doc.signers && doc.signers.length > 1 ? ` +${doc.signers.length - 1}` : ''}
                                            </p>
                                        </div>
                                    </TableCell>
                                    <TableCell>{getStatusBadge(doc)}</TableCell>
                                    <TableCell>{Helper.formatDateTime(doc.createdAt)}</TableCell>
                                    <TableCell>
                                        <div className='flex justify-end gap-2'>
                                            <Button asChild type='button' size='sm' variant='outline'>
                                                <Link to={hasListing ? `/documents/view/${doc.listingId}/${doc.id}` : `/documents/view/${doc.id}`}>
                                                    <Edit className='text-black'/>
                                                </Link>
                                            </Button>
                                            {hasListing && (
                                                <Button
                                                    type='button'
                                                    size='sm'
                                                    variant='destructive'

                                                    onClick={() => setDocumentToDelete(doc)}
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={7} className='py-4 text-center'>
                                No documents found.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <div className='flex justify-end'>
                <Pagination totalPage={totalPages} currentPage={page} onPageChange={setPage} />
            </div>

            <AlertDialog open={!!documentToDelete} onOpenChange={(open) => !open && setDocumentToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Document?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This document will be removed from your listing documents. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    {documentToDelete && (
                        <div className='rounded-lg border border-slate-200 bg-slate-50 p-4'>
                            <p className='font-medium text-slate-900'>{templateName(documentToDelete.documentTemplateId)}</p>
                            <p className='mt-1 text-sm text-slate-500'>
                                {documentToDelete.listing?.listingReferenceNumber || '-'}
                            </p>
                        </div>
                    )}

                    <AlertDialogFooter>
                        <AlertDialogCancel className='cursor-pointer' disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className='cursor-pointer bg-destructive hover:bg-destructive/90'
                            onClick={(e) => {
                                e.preventDefault();
                                handleDeleteDocument();
                            }}
                            disabled={deleting}
                        >
                            {deleting ? 'Deleting...' : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default DocumentsPage;
